import { type ExternalFile } from '@/lib/types';
import UploadWithOneDrive from './UploadWithOneDrive';
import UploadWithGoogleDrive from './UploadWithGoogleDrive';
import { useEffect, useRef } from 'react';
import MenuWithLazyPersistedMounting from '@/components/Menu/MenuWithLazyPersistedMounting';

type UploadOptionsMenuProps = {
  id: string;
  open: boolean;
  anchorEl: HTMLLIElement | null;
  onClose: () => void;
  parentMenuItemId: string;
  onAttachUploadedFile: (uploadedFile: ExternalFile) => void;
  onRegisterAddButtonLoadStart: () => string;
  onRegisterAddButtonLoadEnd: (loaderId: string) => void;
  onRegisterAddButtonError: () => string;
  onRemoveAddButtonError: (errorId: string) => void;
  onRegisterUploadOptionsLoadStart: () => string;
  onRegisterUploadOptionsLoadEnd: (loaderId: string) => void;
  onRegisterUploadOptionsError: () => string;
  onRemoveUploadOptionsError: (errorId: string) => void;
};

const UploadOptionsMenu = ({
  id,
  open,
  anchorEl,
  onClose,
  parentMenuItemId,
  onAttachUploadedFile,
  onRegisterAddButtonLoadStart,
  onRegisterAddButtonLoadEnd,
  onRegisterAddButtonError,
  onRemoveAddButtonError,
  onRegisterUploadOptionsLoadStart,
  onRegisterUploadOptionsLoadEnd,
  onRegisterUploadOptionsError,
  onRemoveUploadOptionsError,
}: UploadOptionsMenuProps) => {
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (open && firstMenuItemRef.current !== null) {
      firstMenuItemRef.current.focus();
    }
  }, [open]);

  const handleItemLoadStart = (): [string, string] => {
    return [onRegisterAddButtonLoadStart(), onRegisterUploadOptionsLoadStart()];
  };

  const handleItemLoadEnd = ([addButtonLoaderId, uploadOptionsLoaderId]: [
    string,
    string,
  ]): void => {
    onRegisterAddButtonLoadEnd(addButtonLoaderId);
    onRegisterUploadOptionsLoadEnd(uploadOptionsLoaderId);
  };

  const handleRegisterItemError = (): [string, string] => {
    return [onRegisterAddButtonError(), onRegisterUploadOptionsError()];
  };

  const handleRemoveItemError = ([addButtonErrorId, uploadOptionsErrorId]: [
    string,
    string,
  ]): void => {
    onRemoveAddButtonError(addButtonErrorId);
    onRemoveUploadOptionsError(uploadOptionsErrorId);
  };

  return (
    <MenuWithLazyPersistedMounting
      id={id}
      labelledById={parentMenuItemId}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        list: {
          onKeyDown: (e: React.KeyboardEvent<HTMLUListElement>) => {
            if (e.key === 'ArrowLeft') {
              onClose();

              anchorEl?.focus();
            }
          },
        },
      }}
    >
      <UploadWithGoogleDrive
        onAttachUploadedFile={onAttachUploadedFile}
        onLoadStart={handleItemLoadStart}
        onLoadEnd={handleItemLoadEnd}
        onRegisterError={handleRegisterItemError}
        onRemoveError={handleRemoveItemError}
        ref={firstMenuItemRef}
      />
      <UploadWithOneDrive
        onAttachUploadedFile={onAttachUploadedFile}
        onLoadStart={handleItemLoadStart}
        onLoadEnd={handleItemLoadEnd}
        onRegisterError={handleRegisterItemError}
        onRemoveError={handleRemoveItemError}
      />
    </MenuWithLazyPersistedMounting>
  );
};

export default UploadOptionsMenu;
